import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useBizMutation, useBizQuery } from '../../Hooks/useBizQuery';
import { executeCatalogCommand, getCatalogConfiguration } from '../../Services/mediaCatalog';
import { useMediaCatalog } from '../../Hooks/useMediaCatalog';
import {
	AnalysisSettings,
	CatalogFilter,
	CatalogItem,
	Classification,
	MediaContentCategory,
	VirtualFolder,
} from '../../Types/mediaCatalog';
import { MediabankFile, MediabankUploadLocation } from '../../Types/mediabank';
import { getThumbPath } from '../../Services/mediabank';
import { BizNativeSelect } from '../Mini/BizSelect';
import { BizButton } from '../Mini/BizButton';
import DebouncedInput from '../Mini/DebouncedInput';
import './media-catalog.scss';
import { getMediaFolderOptions } from '../../Utils/mediaFolders';

type Props = {
	onSelect?: (file: MediabankFile) => void;
	onConfirmSelect?: (file: MediabankFile) => void;
	selectedFileId?: number;
	onEdit?: (id: number) => void;
	initialType?: number;
	initialCategory?: MediaContentCategory;
	legacy?: boolean;
};
const statusLabels: Record<string, string> = {
	unscanned: 'MEDIABANK.CATALOG.STATUS_UNSCANNED',
	queued: 'MEDIABANK.CATALOG.STATUS_QUEUED',
	running: 'MEDIABANK.CATALOG.STATUS_RUNNING',
	complete: 'MEDIABANK.CATALOG.STATUS_COMPLETE',
	failed: 'MEDIABANK.CATALOG.STATUS_FAILED',
	unsupported: 'MEDIABANK.CATALOG.STATUS_UNSUPPORTED',
};
const uploadLocationLabels: Record<MediabankUploadLocation, string> = {
	[MediabankUploadLocation.Undefined]: 'UNDEFINED',
	[MediabankUploadLocation.MediabankAdmin]: 'MEDIABANK_ADMIN',
	[MediabankUploadLocation.SpeedquizAdmin]: 'SPEEDQUIZ_ADMIN',
	[MediabankUploadLocation.CMS]: 'CMS',
	[MediabankUploadLocation.OnlineEducation]: 'ONLINE_EDUCATION',
	[MediabankUploadLocation.LevelSystem]: 'LEVEL_SYSTEM',
	[MediabankUploadLocation.UserProfile]: 'USER_PROFILE',
	[MediabankUploadLocation.Speedquiz]: 'SPEEDQUIZ',
	[MediabankUploadLocation.Ticket]: 'TICKET',
	[MediabankUploadLocation.Goal]: 'GOAL',
	[MediabankUploadLocation.GenericComment]: 'GENERIC_COMMENT',
	[MediabankUploadLocation.FrontEndObject]: 'FRONTEND_OBJECT',
	[MediabankUploadLocation.DocumentCategory]: 'DOCUMENT_CATEGORY',
	[MediabankUploadLocation.DocumentEdit]: 'DOCUMENT_EDIT',
	[MediabankUploadLocation.EventAdmin]: 'EVENT_ADMIN',
	[MediabankUploadLocation.Whiteboard]: 'WHITEBOARD',
	[MediabankUploadLocation.Badges]: 'BADGES',
	[MediabankUploadLocation.TimeReportExpense]: 'TIME_REPORT_EXPENSE',
	[MediabankUploadLocation.UserAdmin]: 'USER_ADMIN',
};
const uploadLocations = Object.values(MediabankUploadLocation).filter(
	(value): value is MediabankUploadLocation => typeof value === 'number'
);

export default function MediaCatalogBrowser({
	onSelect,
	onConfirmSelect,
	selectedFileId,
	onEdit,
	initialType = 0,
	initialCategory,
	legacy,
}: Props) {
	const { t } = useTranslation();
	const tagDialogTitleId = useId();
	const categoryLabel = (category: string) => t(`MEDIABANK.CATALOG.CATEGORY_${category.toUpperCase()}`, category);
	const [filter, setFilter] = useState<CatalogFilter>({
		searchTerm: '',
		folderId: null,
		includeDescendants: false,
		category: initialCategory ?? '',
		status: '',
		type: initialType,
		top: 60,
	});
	const [selected, setSelected] = useState<number[]>([]);
	const [folderEdit, setFolderEdit] = useState<VirtualFolder | null>(null);
	const [destination, setDestination] = useState<number | null>(null);
	const [tagEdit, setTagEdit] = useState<CatalogItem | null>(null);
	const { configuration, files, command } = useMediaCatalog(filter);
	const folders = configuration.data?.folders ?? [];
	const folderOptions = getMediaFolderOptions(folders);
	const updateFilter = (values: Partial<CatalogFilter>) => {
		setFilter((current) => ({ ...current, ...values, top: 60 }));
		setSelected([]);
	};
	const currentFolder = folders.find((f) => f.id === filter.folderId);
	const busy = command.isPending;
	const analysisSettings = configuration.data?.settings;
	const canAnalyze = (item: CatalogItem) =>
		analysisSettings?.allUploadLocations === true ||
		analysisSettings?.uploadLocations.includes(item.file.UploadLocation ?? MediabankUploadLocation.Undefined) === true;
	const visibleFileIds = files.data?.map((item) => item.file.Id) ?? [];
	const selectableFileIds = visibleFileIds.slice(0, 500);
	const allVisibleSelected = selectableFileIds.length > 0 && selectableFileIds.every((id) => selected.includes(id));
	const selectedForAnalysis = files.data?.filter((item) => selected.includes(item.file.Id) && canAnalyze(item)).map((item) => item.file.Id) ?? [];
	const activatePickerItem = (file: MediabankFile) => {
		if (!onSelect) return;
		if (selectedFileId === file.Id && onConfirmSelect) {
			onConfirmSelect(file);
			return;
		}
		onSelect(file);
	};
	const renderFolders = (parentId: number | null, ancestors: number[] = []): React.ReactNode => (
		<ul>
			{folders
				.filter((f) => f.parentId === parentId && !ancestors.includes(f.id))
				.map((folder) => (
					<li key={folder.id}>
						<button
							type='button'
							aria-current={filter.folderId === folder.id ? 'page' : undefined}
							onClick={() => updateFilter({ folderId: folder.id })}
						>
							{folder.name}
						</button>
						{renderFolders(folder.id, [...ancestors, folder.id])}
					</li>
				))}
		</ul>
	);

	return (
		<div className={`biz-media-catalog${onSelect ? ' biz-media-catalog--picker' : ''}`}>
			{command.isSuccess && (
				<p role='status'>
					{command.variables?.action === 'scan' || command.variables?.action === 'scanUnscanned'
						? t('MEDIABANK.CATALOG.ANALYSIS_QUEUED')
						: t('MEDIABANK.CATALOG.CHANGES_SAVED')}
				</p>
			)}
			{(configuration.errorMessage || files.errorMessage || command.errorMessage) && (
				<p role='alert'>{configuration.errorMessage || files.errorMessage || command.errorMessage}</p>
			)}
			<div className='catalog-layout'>
				{!onSelect && (
					<nav aria-label={t('MEDIABANK.CATALOG.FOLDERS_ARIA')}>
						<button
							type='button'
							aria-current={filter.folderId === null ? 'page' : undefined}
							onClick={() => updateFilter({ folderId: null })}
						>
							{t('MEDIABANK.CATALOG.ALL_FILES')}
						</button>
						<button
							type='button'
							aria-current={filter.folderId === 0 ? 'page' : undefined}
							onClick={() => updateFilter({ folderId: 0 })}
						>
							{t('MEDIABANK.CATALOG.UNSORTED')}
						</button>
						{renderFolders(null)}
						{!onSelect && (
							<div className='catalog-actions'>
								<BizButton
									text={t('MEDIABANK.CATALOG.NEW_FOLDER')}
									legacy={legacy}
									onClick={() => setFolderEdit({ id: 0, parentId: currentFolder?.id ?? null, name: '' })}
								/>
								{currentFolder && (
									<BizButton
										text={t('MEDIABANK.CATALOG.EDIT_FOLDER')}
										legacy={legacy}
										onClick={() => setFolderEdit({ ...currentFolder })}
									/>
								)}
							</div>
						)}
					</nav>
				)}
				<section aria-label={t('MEDIABANK.CATALOG.FILES_ARIA')}>
					<div className='catalog-filters'>
						{onSelect && folders.length > 0 && (
							<label>
								{t('MEDIABANK.CATALOG.FOLDER')}
								<BizNativeSelect
									selectedId={filter.folderId ?? -1}
									onChange={(o) =>
										updateFilter({
											folderId: Number(o?.Id) === -1 ? null : Number(o?.Id),
											includeDescendants: false,
										})
									}
									options={[
										{ Id: -1, Name: t('MEDIABANK.CATALOG.ALL_FILES') },
										{ Id: 0, Name: t('MEDIABANK.CATALOG.UNSORTED') },
										...folderOptions,
									]}
								/>
							</label>
						)}
						<DebouncedInput
							placeholder={t('MEDIABANK.CATALOG.SEARCH_PLACEHOLDER')}
							onChange={(searchTerm) => updateFilter({ searchTerm })}
						/>
						<label>
							{t('MEDIABANK.CATALOG.CONTENT')}
							<BizNativeSelect
								selectedId={filter.category}
								onChange={(o) => updateFilter({ category: String(o?.Id ?? '') })}
								options={[
									{ Id: '', Name: t('MEDIABANK.CATALOG.ALL_CATEGORIES') },
									...(configuration.data?.categories ?? []).map((c) => ({ Id: c, Name: categoryLabel(c) })),
								]}
							/>
						</label>
						{!onSelect && (
							<label>
								{t('MEDIABANK.CATALOG.FILE_TYPE')}
								<BizNativeSelect
									selectedId={filter.type}
									onChange={(o) => updateFilter({ type: Number(o?.Id ?? 0) })}
									options={[
										{ Id: 0, Name: t('MEDIABANK.CATALOG.ALL') },
										{ Id: 2, Name: t('MEDIABANK.CATALOG.IMAGES') },
										{ Id: 1, Name: t('MEDIABANK.CATALOG.VIDEO') },
										{ Id: 3, Name: t('MEDIABANK.CATALOG.AUDIO') },
										{ Id: 4, Name: t('MEDIABANK.CATALOG.DOCUMENTS') },
									]}
								/>
							</label>
						)}
						{!onSelect && (
							<>
								<label>
									{t('MEDIABANK.CATALOG.ANALYSIS_STATUS')}
									<BizNativeSelect
										selectedId={filter.status}
										onChange={(o) => updateFilter({ status: String(o?.Id ?? '') })}
										options={[
											{ Id: '', Name: t('MEDIABANK.CATALOG.ALL_STATUSES') },
											...Object.entries(statusLabels).map(([Id, key]) => ({ Id, Name: t(key) })),
										]}
									/>
								</label>
								<label>
									{t('MEDIABANK.CATALOG.DATE_FROM')}
									<input
										type='date'
										value={filter.dateFrom ?? ''}
										onChange={(e) => updateFilter({ dateFrom: e.target.value || undefined })}
									/>
								</label>
								<label>
									{t('MEDIABANK.CATALOG.DATE_TO')}
									<input
										type='date'
										value={filter.dateTo ?? ''}
										onChange={(e) => updateFilter({ dateTo: e.target.value || undefined })}
									/>
								</label>
							</>
						)}
						{!onSelect && currentFolder && (
							<label>
								<input
									type='checkbox'
									checked={filter.includeDescendants}
									onChange={(e) => updateFilter({ includeDescendants: e.target.checked })}
								/>{' '}
								{t('MEDIABANK.CATALOG.INCLUDE_SUBFOLDERS')}
							</label>
						)}
					</div>
					{!onSelect && (
						<div className='catalog-actions'>
							<label>
								{t('MEDIABANK.CATALOG.MOVE_TO')}
								<BizNativeSelect
									selectedId={destination ?? 0}
									onChange={(o) => setDestination(Number(o?.Id) || null)}
									options={[
										{ Id: 0, Name: t('MEDIABANK.CATALOG.UNSORTED') },
										...folderOptions,
									]}
								/>
							</label>
							<BizButton
								text={t('MEDIABANK.CATALOG.MOVE_SELECTED', { count: selected.length })}
								legacy={legacy}
								disabled={!selected.length || busy}
								onClick={() => command.mutate({ action: 'place', fileIds: selected, folderId: destination })}
							/>
							<BizButton
								text={t('MEDIABANK.CATALOG.REANALYZE_SELECTED', { count: selectedForAnalysis.length })}
								legacy={legacy}
								disabled={!selectedForAnalysis.length || busy || !configuration.data?.settings.enabled}
								onClick={() => command.mutate({ action: 'scan', fileIds: selectedForAnalysis })}
							/>
							<BizButton
								text={t('MEDIABANK.CATALOG.ANALYZE_ALL_UNSCANNED')}
								legacy={legacy}
								disabled={busy || !configuration.data?.settings.enabled}
								onClick={() => command.mutate({ action: 'scanUnscanned' })}
							/>
							{configuration.data && !configuration.data.settings.enabled && (
								<p className='catalog-analysis-disabled' role='status'>
									{t('MEDIABANK.CATALOG.ANALYSIS_DISABLED')}
								</p>
							)}
						</div>
					)}
					{files.statusState === 'LOADING' && <p role='status'>{t('MEDIABANK.CATALOG.LOADING_FILES')}</p>}
					{files.statusState === 'SUCCESS' && !files.data?.length && <p>{t('MEDIABANK.CATALOG.NO_MATCHES')}</p>}
					{!onSelect && visibleFileIds.length > 0 && (
						<label className='catalog-select-all'>
							<input
								type='checkbox'
								checked={allVisibleSelected}
								onChange={(e) => setSelected(e.target.checked ? selectableFileIds : [])}
							/>{' '}
							{visibleFileIds.length > 500
								? t('MEDIABANK.CATALOG.SELECT_FIRST', { count: 500 })
								: t('MEDIABANK.CATALOG.SELECT_ALL')}
						</label>
					)}
					<div className='catalog-grid'>
						{files.data?.map((item) => (
							<article
								key={item.file.Id}
								className={onSelect && selectedFileId === item.file.Id ? 'catalog-item--selected' : undefined}
								role={onSelect ? 'button' : undefined}
								tabIndex={onSelect ? 0 : undefined}
								aria-pressed={onSelect ? selectedFileId === item.file.Id : undefined}
								onClick={() => onSelect && activatePickerItem(item.file)}
								onKeyDown={(event) => {
									if (onSelect && (event.key === 'Enter' || event.key === ' ')) {
										event.preventDefault();
										activatePickerItem(item.file);
									}
								}}
							>
								{!onSelect && (
									<label>
										<input
											type='checkbox'
											aria-label={t('MEDIABANK.CATALOG.SELECT_FILE', { name: item.file.Name })}
											disabled={!selected.includes(item.file.Id) && selected.length >= 500}
											checked={selected.includes(item.file.Id)}
											onChange={(e) =>
												setSelected((current) =>
													e.target.checked
														? [...current, item.file.Id]
														: current.filter((id) => id !== item.file.Id)
												)
											}
										/>{' '}
									</label>
								)}
								{onSelect ? (
									<div className='catalog-preview'>
										<img loading='lazy' src={getThumbPath(item.file.Thumbnail || item.file.FileUrl)} alt='' />
										<strong>{item.file.Name}</strong>
									</div>
								) : (
									<button type='button' className='catalog-preview' onClick={() => setTagEdit(item)}>
										<img loading='lazy' src={getThumbPath(item.file.Thumbnail || item.file.FileUrl)} alt='' />
										<strong>{item.file.Name}</strong>
									</button>
								)}
								<p>{item.classification.categories.map(categoryLabel).join(', ')}</p>
								{!onSelect && (
									<>
										<p>{item.classification.tags.join(', ')}</p>
										<div className='catalog-analysis-status'>
											<small className={`catalog-status-badge catalog-status-badge--${item.classification.status}`}>
											{statusLabels[item.classification.status]
												? t(statusLabels[item.classification.status])
												: item.classification.status}
											</small>
										{analysisSettings?.enabled && !canAnalyze(item) && (
											<small>{t('MEDIABANK.CATALOG.EXCLUDED_FROM_ANALYSIS')}</small>
										)}
										</div>
										{item.classification.error && <p>{item.classification.error}</p>}
									</>
								)}
								{!onSelect && (
									<div className='catalog-actions'>
									<BizButton text={t('MEDIABANK.CATALOG.KEYWORDS')} legacy={legacy} onClick={() => setTagEdit(item)} />
										{onEdit && (
										<BizButton text={t('MEDIABANK.CATALOG.EDIT')} legacy={legacy} onClick={() => onEdit(item.file.Id)} />
										)}
									</div>
								)}
							</article>
						))}
					</div>
					{files.data?.length === filter.top && filter.top < 1000 && (
						<BizButton
							text={t('MEDIABANK.CATALOG.SHOW_MORE')}
							legacy={legacy}
							onClick={() => setFilter((current) => ({ ...current, top: Math.min(current.top + 60, 1000) }))}
						/>
					)}
					{files.data?.length === 1000 && (
						<p>{t('MEDIABANK.CATALOG.RESULT_LIMIT', { count: 1000 })}</p>
					)}
				</section>
			</div>
			{folderEdit && (
				<section className='catalog-editor' aria-label={t('MEDIABANK.CATALOG.EDIT_FOLDER')}>
					<label>
						{t('MEDIABANK.CATALOG.FOLDER_NAME')}
						<input
							value={folderEdit.name}
							maxLength={200}
							onChange={(e) => setFolderEdit({ ...folderEdit, name: e.target.value })}
						/>
					</label>
					<label>
						{t('MEDIABANK.CATALOG.PARENT_FOLDER')}
						<BizNativeSelect
							selectedId={folderEdit.parentId ?? 0}
							onChange={(o) => setFolderEdit({ ...folderEdit, parentId: Number(o?.Id) || null })}
							options={[
								{ Id: 0, Name: t('MEDIABANK.CATALOG.ROOT_LEVEL') },
								...folderOptions.filter((f) => f.Id !== folderEdit.id),
							]}
						/>
					</label>
					<BizButton
						text={t('MEDIABANK.CATALOG.SAVE_FOLDER')}
						legacy={legacy}
						disabled={busy || !folderEdit.name.trim()}
						onClick={() => {
							void command
								.mutateAsync({ action: 'saveFolder', folder: folderEdit })
								.then(() => setFolderEdit(null))
								.catch(() => undefined);
						}}
					/>
					{folderEdit.id > 0 && (
						<BizButton
							text={t('MEDIABANK.CATALOG.DELETE_EMPTY_FOLDER')}
							legacy={legacy}
							disabled={busy}
							onClick={() => {
								void command
									.mutateAsync({ action: 'deleteFolder', folder: folderEdit })
									.then(() => {
										setFolderEdit(null);
										updateFilter({ folderId: null });
									})
									.catch(() => undefined);
							}}
						/>
					)}
					<BizButton text={t('GLOBAL.CLOSE')} legacy={legacy} onClick={() => setFolderEdit(null)} />
				</section>
			)}
			{tagEdit && (
				<Dialog open onClose={() => !busy && setTagEdit(null)} aria-labelledby={tagDialogTitleId} fullWidth maxWidth='sm'>
					<DialogTitle id={tagDialogTitleId}>{t('MEDIABANK.CATALOG.KEYWORDS_TITLE', { name: tagEdit.file.Name })}</DialogTitle>
					<DialogContent>
						<div className='biz-media-catalog'>
							{command.errorMessage && <p role='alert'>{command.errorMessage}</p>}
							<TagEditor
								key={tagEdit.file.Id}
								item={tagEdit}
								categories={configuration.data?.categories ?? []}
								legacy={legacy}
								busy={busy}
								onClose={() => setTagEdit(null)}
								onSave={(classification) => {
									void command
										.mutateAsync({ action: 'classify', classification })
										.then(() => setTagEdit(null))
										.catch(() => undefined);
								}}
							/>
						</div>
					</DialogContent>
				</Dialog>
			)}
		</div>
	);
}

export function MediaAnalysisSettings({ legacy }: { legacy?: boolean }) {
	const { t } = useTranslation();
	const configuration = useBizQuery(['MEDIA_CATALOG', 'CONFIGURATION'], getCatalogConfiguration);
	const command = useBizMutation(executeCatalogCommand, { getQueryKeyForInvalidation: () => [['MEDIA_CATALOG']] });
	const [settingsEdit, setSettingsEdit] = useState<AnalysisSettings | null>(null);
	const busy = command.isPending;
	return (
		<div className='biz-media-catalog'>
			<h3>{t('MEDIABANK.CATALOG.IMAGE_ANALYSIS')}</h3>
			{(configuration.errorMessage || command.errorMessage) && (
				<p role='alert'>{configuration.errorMessage || command.errorMessage}</p>
			)}
			{command.isSuccess && <p role='status'>{t('MEDIABANK.CATALOG.SETTINGS_SAVED')}</p>}
			<BizButton
				text={t('MEDIABANK.CATALOG.ANALYSIS_SETTINGS')}
				legacy={legacy}
				disabled={!configuration.data}
				onClick={() => setSettingsEdit({ ...configuration.data!.settings })}
			/>
			{settingsEdit && (
				<section className='catalog-editor' aria-label={t('MEDIABANK.CATALOG.ANALYSIS_SETTINGS')}>
					<label>
						<input
							type='checkbox'
							checked={settingsEdit.enabled}
							onChange={(e) => setSettingsEdit({ ...settingsEdit, enabled: e.target.checked })}
						/>{' '}
						{t('MEDIABANK.CATALOG.ENABLE_ANALYSIS')}
					</label>
					<label>
						<input
							type='checkbox'
							checked={settingsEdit.allUploadLocations}
							onChange={(e) => setSettingsEdit({ ...settingsEdit, allUploadLocations: e.target.checked })}
						/>{' '}
						{t('MEDIABANK.CATALOG.ALL_UPLOAD_LOCATIONS')}
					</label>
					{!settingsEdit.allUploadLocations && (
						<fieldset className='catalog-upload-locations'>
							<legend>{t('MEDIABANK.CATALOG.ALLOWED_UPLOAD_LOCATIONS')}</legend>
							{uploadLocations.map((location) => (
								<label key={location}>
									<input
										type='checkbox'
										checked={settingsEdit.uploadLocations.includes(location)}
										onChange={(e) =>
											setSettingsEdit({
												...settingsEdit,
												uploadLocations: e.target.checked
													? [...settingsEdit.uploadLocations, location]
													: settingsEdit.uploadLocations.filter((value) => value !== location),
											})
										}
									/>{' '}
									{t(`MEDIABANK.CATALOG.UPLOAD_LOCATION_${uploadLocationLabels[location]}`)}
								</label>
							))}
						</fieldset>
					)}
					<label>
						{t('MEDIABANK.CATALOG.AZURE_MODEL_NAME')}
						<input
							value={settingsEdit.model ?? ''}
							onChange={(e) => setSettingsEdit({ ...settingsEdit, model: e.target.value })}
						/>
					</label>
					<p>{t('MEDIABANK.CATALOG.ANALYSIS_PRIVACY_INFO')}</p>
					<BizButton
						text={t('MEDIABANK.CATALOG.SAVE_SETTINGS')}
						legacy={legacy}
						disabled={busy}
						onClick={() => {
							void command
								.mutateAsync({ action: 'settings', settings: settingsEdit })
								.then(() => setSettingsEdit(null))
								.catch(() => undefined);
						}}
					/>
					<BizButton text={t('GLOBAL.CLOSE')} legacy={legacy} onClick={() => setSettingsEdit(null)} />
				</section>
			)}
		</div>
	);
}

function TagEditor({
	item,
	categories,
	legacy,
	busy,
	onSave,
	onClose,
}: {
	item: CatalogItem;
	categories: string[];
	legacy?: boolean;
	busy: boolean;
	onSave: (value: Classification) => void;
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const [chosen, setChosen] = useState(item.classification.categories);
	const [tags, setTags] = useState(item.classification.tags.join(', '));
	const save = () => {
		const c = item.classification;
		const entered = [
			...new Set(
				tags
					.split(',')
					.map((t) => t.trim().toLowerCase())
					.filter(Boolean)
			),
		];
		onSave({
			...c,
			addedCategories: chosen.filter((t) => c.addedCategories.includes(t) || !c.aiCategories.includes(t)),
			removedCategories: [...new Set([...c.removedCategories, ...c.aiCategories])].filter((t) => !chosen.includes(t)),
			addedTags: entered.filter((t) => c.addedTags.includes(t) || !c.aiTags.includes(t)),
			removedTags: [...new Set([...c.removedTags, ...c.aiTags])].filter((t) => !entered.includes(t)),
		});
	};
	return (
		<section className='catalog-editor' aria-label={t('MEDIABANK.CATALOG.EDIT_KEYWORDS_ARIA')}>
			<h3>{item.file.Name}</h3>
			<p>{t('MEDIABANK.CATALOG.MANUAL_CHANGES_INFO')}</p>
			{categories.map((c) => (
				<label key={c}>
					<input
						type='checkbox'
						checked={chosen.includes(c)}
						onChange={(e) =>
							setChosen((current) => (e.target.checked ? [...current, c] : current.filter((t) => t !== c)))
						}
					/>{' '}
					{t(`MEDIABANK.CATALOG.CATEGORY_${c.toUpperCase()}`, c)}
				</label>
			))}
			<label>
				{t('MEDIABANK.CATALOG.KEYWORDS_COMMA_SEPARATED')}
				<textarea value={tags} onChange={(e) => setTags(e.target.value)} />
			</label>
			<BizButton text={t('MEDIABANK.CATALOG.SAVE_KEYWORDS')} legacy={legacy} disabled={busy} onClick={save} />
			<BizButton text={t('GLOBAL.CLOSE')} legacy={legacy} disabled={busy} onClick={onClose} />
		</section>
	);
}
